<template>
  <div id="app" class="loginimg" style="display: flex; flex-direction: column;">
    <div style="margin: auto 0; width: 100%;">

    <CustomerLogo />

    <div class="row justify-content-center">
      <div class="col-10 col-md-6 col-lg-5 col-xl-4 align-self-center justify-content-center row">
        <div class="white--text col-12 text-h4 text-center">
          {{ $t('welcome_to_muniman') }}
        </div>

        <div class="white--text col-10 text-center mt-4 mb-6" style="opacity: 0.85; font-size: 14px;">
          {{ $t('complete_account_setup') }}
        </div>

        <div class="col-10 pa-0 mt-2 mb-4">
          <div class="white--text mb-2 text-uppercase" style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
            {{ $t('password') }}
          </div>
          <v-text-field
            class="white rounded-0 logininput"
            :type="showPassword ? 'text' : 'password'"
            :label="$t('password')"
            outlined
            hide-details="auto"
            v-model="password"
            @input="errors.password = false"
            :error="errors.password || (password.length > 0 && passwordErrors !== '')">
            <template v-slot:append>
              <v-icon
                small
                @click="showPassword = !showPassword"
                class="cursor-pointer"
              >
                {{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>
          <div v-if="password.length > 0 && passwordErrors" class="error--text" style="font-size: 12px; margin-top: 4px;">
            {{ passwordErrors }}
          </div>
        </div>

        <div class="col-10 pa-0 mb-4">
          <div class="white--text mb-2 text-uppercase" style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
            {{ $t('confirm_password') }}
          </div>
          <v-text-field
            class="white rounded-0 logininput"
            :type="showConfirmPassword ? 'text' : 'password'"
            :label="$t('confirm_password')"
            outlined
            hide-details="auto"
            v-model="confirmPassword"
            @input="errors.confirmPassword = false"
            :error="errors.confirmPassword || (confirmPassword.length > 0 && password !== confirmPassword)">
            <template v-slot:append>
              <v-icon
                small
                @click="showConfirmPassword = !showConfirmPassword"
                class="cursor-pointer"
              >
                {{ showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>
          <div v-if="confirmPassword.length > 0 && password !== confirmPassword" class="error--text" style="font-size: 12px; margin-top: 4px;">
            {{ $t('passwords_do_not_match') }}
          </div>
        </div>

        <!-- Data Privacy Checkbox -->
        <div data-field="privacyPolicy" class="pa-0 col-10 mb-6">
          <v-checkbox
              v-model="privacyPolicyAccepted"
              :error="errors.privacyPolicy"
              hide-details="auto"
              @change="errors.privacyPolicy = false"
              dark
              color="white"
              class="mt-0">
            <template v-slot:label>
              <span class="white--text" style="font-size: 14px; opacity: 0.9;">
                {{ $t('accept_privacy_policy') }}
                <PrivacyPolicy :link-text="$t('privacy_policy_title')" />
                {{ $t('accept_privacy_policy_terms_separator') }}
                <TermsAndConditions :link-text="$t('terms_and_conditions_title')" />
              </span>
            </template>
          </v-checkbox>
        </div>

        <!-- Set Password Button -->
        <div class="pa-0 col-10 mb-4">
          <v-btn
            class="w-100 white--text"
            :class="isFormValid ? 'signinbtn' : ''"
            :style="isFormValid ? 'padding: 27px !important; background-color: #161616 !important;' : 'padding: 27px !important; background-color: #666666 !important; border: 1px solid #666666 !important; cursor: not-allowed !important;'"
            outlined
            tile
            :disabled="!isFormValid"
            @click="setPassword"
            :loading="isLoading">
            {{ $t('set_password') }}
          </v-btn>
        </div>

        <!-- Back to login link -->
        <div class="pa-0 col-10 mb-8 text-center">
          <span class="white--text" style="opacity: 0.7; font-size: 14px;">
            {{ $t('back_to_login') }}
          </span>
          <v-btn
            text
            small
            class="white--text ml-1"
            style="text-transform: none; font-size: 14px; text-decoration: underline;"
            @click="$router.push('/login')">
            {{ $t('log_in') }}
          </v-btn>
        </div>

        <!-- Error message -->
        <div v-if="error" class="col-10 pa-0 mt-4">
          <v-alert type="error" outlined>
            {{ error }}
          </v-alert>
        </div>
      </div>
    </div>

    </div>
  </div>
</template>

<script>
import PrivacyPolicy from './custom/PrivacyPolicy.vue';
import TermsAndConditions from './custom/TermsAndConditions.vue';
import CustomerLogo from "@/components/custom/CustomerLogo.vue";

export default {
  name: 'CompleteRegistration',
  components: {
    CustomerLogo,
    PrivacyPolicy,
    TermsAndConditions
  },

  data() {
    return {
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      privacyPolicyAccepted: false,
      errors: {
        password: false,
        confirmPassword: false,
        privacyPolicy: false
      },
      error: null,
      isLoading: false,
      userId: null,
      code: null
    };
  },

  computed: {
    isFormValid() {
      return this.password && this.confirmPassword &&
             this.password === this.confirmPassword &&
             this.password.length >= 8 &&
             this.userId !== null &&
             this.code !== null &&
             this.privacyPolicyAccepted;
    },

    passwordErrors() {
      if (this.password.length < 8) {
        return this.$t('password_too_short');
      }
      if (!this.hasUpperCase()) {
        return this.$t('password_needs_uppercase');
      }
      if (!this.hasLowerCase()) {
        return this.$t('password_needs_lowercase');
      }
      if (!this.hasNumber()) {
        return this.$t('password_needs_number');
      }
      if (!this.hasSpecialChar()) {
        return this.$t('password_needs_special_char');
      }
      return '';
    }
  },

  mounted() {
    this.initializeFromQuery();
  },

  methods: {
    initializeFromQuery() {
      const query = this.$route.query;
      
      if (!query.u || !query.c) {
        this.error = this.$t('invalid_registration_link');
        return;
      }

      this.userId = query.u;
      this.code = query.c;
    },

    hasUpperCase() {
      return /[A-Z]/.test(this.password);
    },

    hasLowerCase() {
      return /[a-z]/.test(this.password);
    },

    hasNumber() {
      return /[0-9]/.test(this.password);
    },

    hasSpecialChar() {
      return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.password);
    },

    validate() {
      this.errors = {
        password: false,
        confirmPassword: false,
        privacyPolicy: false
      };

      if (!this.password) {
        this.errors.password = true;
        return false;
      }

      if (this.password.length < 8) {
        this.errors.password = true;
        return false;
      }

      if (!this.hasUpperCase() || !this.hasLowerCase() || !this.hasNumber() || !this.hasSpecialChar()) {
        this.errors.password = true;
        return false;
      }

      if (!this.confirmPassword) {
        this.errors.confirmPassword = true;
        return false;
      }

      if (this.password !== this.confirmPassword) {
        this.errors.confirmPassword = true;
        return false;
      }

      if (!this.privacyPolicyAccepted) {
        this.errors.privacyPolicy = true;
        return false;
      }

      return true;
    },

    setPassword() {
      if (!this.validate()) {
        if (!this.privacyPolicyAccepted) {
          this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('privacy_policy_required'));
        } else {
          this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('password_requirements_not_met'));
        }
        return;
      }

      this.isLoading = true;
      this.error = null;

      const request = {
        userId: this.userId,
        code: this.code,
        password: this.password,
        confirmPassword: this.confirmPassword
      };

      this.$axios.patch('/api/register/complete', request)
        .then(response => {
          // Show success notification and redirect after it auto-dismisses (6 seconds)
          this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + this.$t('password_set_successfully'));

          // Redirect to login with firstLogin flag for subscription flow
          // Wait for notification to disappear (6000ms timeout from VueNoty config)
          setTimeout(() => {
            this.$router.push({
              path: '/login',
              query: { firstLogin: 'true' }
            });
          }, 6200);
        })
        .catch(error => {
          console.error('Registration completion error:', error);

          let errorMsg = this.$t('password_set_failed');

          if (error.response) {
            const status = error.response.status;
            const backendMessage = error.response.data?.message;

            if (status === 400) {
              if (backendMessage === "ERROR_USER_NOT_PENDING_PASSWORD") {
                errorMsg = this.$t('user_passwort_already_set');
              } else {
                errorMsg = this.$t('invalid_password_or_code');
              }
            } else if (status === 401) {
              errorMsg = this.$t('invalid_code');
            } else if (status === 404) {
              errorMsg = this.$t('registration_link_expired');
            } else if (status === 409) {
              errorMsg = this.$t('invalid_registration_link');
            } else if (status === 410) {
              errorMsg = this.$t('registration_link_expired');
            } else if (status === 412) {
              errorMsg = this.$t('ERROR_INVITATION_CODE_EXPIRED');
            } else if (status === 500) {
              errorMsg = this.$t('server_error_try_again');
            }
          } else {
            errorMsg = this.$t('network_error');
          }

          this.error = errorMsg;
          this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + errorMsg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.error--text {
  color: #ff6363 !important;
}
</style>
